<template>
  <div>
    <loader v-if="loading"></loader>
    <section v-if="!loading">
      <div class="row card-body">
        <div class="col-lg-6 pr-lg-4">
          <div class="d-flex align-items-center justify-content-center">
            <div class="user-avatar">
              <img :src="boxImg" alt="profile image" class="img-fluid" />
              <smart-oee-photo-upload @uploaded="uploaded"></smart-oee-photo-upload>
            </div>
          </div>
        </div>
        <div class="col-lg-6">
          <div class="justify-content-between pb-2">
            <div class="page-header mb-0">
              <div class="page-title">
                <box-publisher></box-publisher>
              </div>
              <div>
                <box-dropdown-menu
                  :box-id="item.id"
                  :box-name="item.digitalThing.name"
                ></box-dropdown-menu>
              </div>
            </div>
          </div>
          <settings-box-form
            v-if="item"
            :item="item"
            :employees="responsibleEmployees"
            :access-to-change="accessToChange"
            @update="formUpdate"
          />
        </div>
      </div>
      <div class="col-lg-12">
        <div class="d-block px-3 pb-4">
          <div>
            <div class="page-header border-bottom">
              <p class="page-title">{{ $t('box.description') }}</p>
            </div>
            <b-form-textarea
              v-model="description"
              placeholder="Enter content"
              :disabled="!accessToChange"
              :rows="3"
              :max-rows="6"
            ></b-form-textarea>
          </div>
          <div v-if="accessToChange" class="text-right pt-4">
            <b-btn variant="danger" class="btn-icon-text" @click="cancel">
              {{ $t('button.cancel.label') }}
            </b-btn>
            <b-btn variant="success" class="btn-icon-text ml-1" @click="save">
              {{ $t('button.save.label') }}
            </b-btn>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import defaultImage from '@/assets/images/KOEEBOX.png';
import boxeQuery from '@/api/boxinfo/boxInfoSettings.gql';
import responsibleEmployeesQuery from '@/api/boxinfo/responsibleEmployees.gql';
import editBoxMutation from '@/api/boxinfo/mutations/editBox.gql';
import boxCardsQuery from '@/api/boxCards/boxes.gql';
import addWorkCenterPhoto from '@/api/boxinfo/mutations/addWorkCenterPhoto.gql';

export default {
  name: 'SmartOeeSettingsBoxEdit',
  props: {
    accessToChange: {
      required: true,
      type: Boolean,
    },
  },
  data() {
    return {
      boxeQuery: boxeQuery,
      responsibleEmployeesQuery: responsibleEmployeesQuery,
      formDescription: '',
      editMode: false,
      formData: {},
      mutationLoading: false,
      item: {},
    };
  },
  apollo: {
    item: {
      query() {
        return this.boxeQuery;
      },
      variables() {
        return {
          deviceId: this.deviceIdInternal,
        };
      },
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        if (queryData && queryData.boxes && queryData.boxes.length) {
          return queryData.boxes[0];
        } else {
          this.$store.commit('SET_ERROR', this.$t('box.error.equipment'));
          return null;
        }
      },
      skip() {
        return !this.deviceIdInternal;
      },
      error(e) {
        this.$store.commit('SET_ERROR', e.message);
      },
    },
    responsibleEmployees: {
      query() {
        return this.responsibleEmployeesQuery;
      },
      update(data) {
        const queryData = data[Object.keys(data)[0]];
        if (
          queryData &&
          queryData.availableOrgEmployees &&
          queryData.availableOrgEmployees.length
        ) {
          return queryData.availableOrgEmployees;
        } else {
          this.$store.commit('SET_ERROR', this.$t('box.error.user'));
          return [];
        }
      },
      error(e) {
        this.$store.commit('SET_ERROR', e.message);
      },
    },
  },
  computed: {
    loading() {
      return this.$apollo.queries.item.loading || this.mutationLoading;
    },
    description: {
      get: function () {
        if (this.formDescription) {
          return this.formDescription;
        }

        if (!this.item) {
          return '';
        }

        return this.item.digitalThing.workCenter.comment;
      },
      set: function (v) {
        this.formDescription = v;
      },
    },
    deviceIdInternal() {
      return this.$route.params.boxId;
    },
    boxImg() {
      const attachmentImage = this.item && this.item.digitalThing.workCenter.attachmentImage;
      return attachmentImage ? attachmentImage.url : defaultImage;
    },
  },
  methods: {
    formUpdate(data) {
      this.formData = data;
    },
    cancel() {
      this.toBoxView();
    },
    save() {
      if (!this.formData.isValid) {
        return;
      }
      this.mutationLoading = true;
      this.$apollo
        .mutate({
          mutation: editBoxMutation,
          variables: {
            editParams: {
              id: this.formData.form.id,
              name: this.formData.form.name,
              itObject: this.formData.form.itObject,
              responsibleEmployee: this.formData.form.responsibleEmployee,
              type: Number(this.formData.form.boxEquipmentType),
              nominalPower: this.formData.form.nominalPower,
              targetAvailability: this.formData.form.targetAvailability,
              targetPerformance: this.formData.form.targetPerformance,
              targetEfficiency: this.formData.form.targetEfficiency,
              description: this.description,
            },
          },
          refetchQueries: [
            {
              query: boxeQuery,
              variables: {
                deviceId: this.deviceIdInternal,
              },
            },
            {
              query: boxCardsQuery,
            },
          ],
        })
        .then(() => {
          this.mutationLoading = false;
          this.$bvToast.toast(this.$t('settings.save'), {
            title: this.$t('toast.success'),
            variant: 'success',
            solid: true,
          });
        })
        .catch((e) => {
          this.mutationLoading = false;
          this.$bvToast.toast(this.$t('settings.failSaved'), {
            title: this.$t('toast.error'),
            variant: 'danger',
            solid: true,
          });
          console.log(e.message);
        });
    },
    uploaded(payload) {
      this.$apollo
        .mutate({
          mutation: addWorkCenterPhoto,
          variables: {
            id: this.item.digitalThing.workCenter.id,
            fileName: payload.fileName,
            filePath: payload.filePath,
          },
          refetchQueries: [
            {
              query: boxeQuery,
              variables: {
                deviceId: this.deviceIdInternal,
              },
            },
            {
              query: boxCardsQuery,
            },
          ],

          awaitRefetchQueries: true,
        })
        .then(() => {
          this.$bvToast.toast(this.$t('settings.save'), {
            title: this.$t('toast.success'),
            variant: 'success',
            solid: true,
          });
        })
        .catch((e) => {
          this.$bvToast.toast(this.$t('settings.failSaved'), {
            title: this.$t('toast.error'),
            variant: 'danger',
            solid: true,
          });
          console.log(e.message);
        });
    },
  },
};
</script>

<style></style>
